import { BaseModel } from '@Core';
import { CameraExemptionPeriodAuditEvent } from './CameraExemptionPeriodAudit';
import { CameraPhotos } from './CameraPhotos';
import { ExemptionPeriods } from './ExemptionPeriods';
import { GeoZones } from './GeoZones';
import { Solar4gCamera } from './Solar4gCamera';
import { Vendor } from './Vendor';
import { Zones } from './Zones';
import { CameraInstalledPhotos } from './CameraInstalledPhotos';
import { PTZCamera } from './PTZCamera';
import { CameraView } from './CameraView';
import { SP111Camera } from './SP111Cameras';
import { PTZCameraConfigs } from './PTZCameraConfigs';
import { CameraTask } from './CameraTask';
import { ParkingSpace } from './ParkingSpace';
import { CameraSIM } from './CameraSIM';
import { Location } from './Locations';
import { PMC8266Camera } from './PMC8266Camera';
import { CameraEvPermitZone } from './CameraEvPermitZone';

export interface CameraWithInfo extends Camera {
    ZoneName?: string;
    LocationName?: string;
    Mac?: string;
    LatestPhoto?: Date;
    IsAbleChangeZone?: boolean;
    ObscuredPlatePhotoCount?: number;
    ExtendedCoverage?: Location[];
    PTZCameraConfig?: PTZCameraConfigs;
    EvPermitZoneNames?: string;
}

export interface StatisticTotal {
    TotalStepOne: number;
    TotalSteTwo: number;
    TotalStepWip: number;
}

export interface Camera extends BaseModel {
    ZoneId: number;
    Zone?: Zones;

    Name: string;

    GMT?: number;

    Status: Status;
    InactiveStatusAt?: Date;

    DeployedDate: Date;

    VendorId: number;
    Vendor?: Vendor;

    VendorCameraId?: number;
    Solar4gCamera?: Solar4gCamera;

    PTZCameraId?: number;
    PTZCamera?: PTZCamera;

    SP111CameraId?: number;
    SP111Camera?: SP111Camera;

    PMC8266CameraId?: number;
    PMC8266Camera?: PMC8266Camera;

    TimeZone?: string;

    IP: string;

    UserName: string;

    PassWord: string;

    LensCapacity?: string;

    ServiceType: CameraServiceType;
    ANPRCameraId: number | null;

    GeoZones?: GeoZones[];

    CameraPhotos?: CameraPhotos[];

    Notes?: string;

    Asset?: string;

    LastPhotoId?: number;
    CurrentPhoto?: CameraPhotos;

    PurposeType?: PurposeType;

    OriginalPhotoId?: number;
    OriginalPhoto?: CameraPhotos;

    LastEditedPhotoId?: number;
    LastEditedPhoto?: CameraPhotos;

    PcnByCamera?: number;
    ExemptionPeriods?: ExemptionPeriods[];
    ExemptionPeriodAuditEvents?: CameraExemptionPeriodAuditEvent[];
    IsVerificationsEnabled?: boolean;
    IsSendEVPermitEnabled?: boolean;
    EvPermitLengthSeconds?: number;
    EvPermitZones?: CameraEvPermitZone[];
    EnablePcnVerificationAt?: Date;
    InactiveVerificationsEnabledAt?: Date;

    CameraInstalledPhotos?: CameraInstalledPhotos[];

    SolarPanelPosition?: SolarPanelPositionType;

    PowerSupply?: PowerSupply;

    Priority?: CameraPriority;

    CameraViews?: CameraView[];

    EnableGroupMatchingAt?: Date;
    LastCheckAngleAt?: Date;

    PcnCurrentMonth?: number;
    PcnThreeMonthsAgo?: number;
    PcnLastMonthAgo?: number;

    CameraTasks?: CameraTask[];

    ParkingSpaceId?: number;
    ParkingSpace?: ParkingSpace;

    CameraSIM?: CameraSIM;
}

export enum Status {
    Active,
    Inactive,
    NotAvailable,
    Deleted,
}

export enum PurposeType {
    Prospective,
    Testing,
    Live,
    OnHold,
    Decommissioned,
    Deleted,
}

export enum BatteryStatus {
    Normal,
    Low,
    Critical,
}

export enum WifiStatus {
    DisConnected,
    Connected,
}

export enum CameraServiceType {
    Solar4G, // ECamTechOps
    ANPR, // ANPRTechOps
    ANPR_V2, // ANPRTechOpsV2
    PTZ, // ECamTechOps_V2
    SP111, // ECamTechOps_V3
    PMC8266, // ECamTechOps_V4
}

export enum SolarPanelPositionType {
    NNE, // (North-North-East)

    ENE, // (East-North-East)

    ESE, // (East-South-East)

    SSE, // (South-South-East)

    SSW, // (South-South-West)

    WSW, // (West-South-West)

    WNW, // (West-North-West)

    NNW, // (North-North-west)
}

export enum PowerSupply {
    Hybrid, // (Hybrid)
    Wired, // (Wired)
    Kit180Wh, // (45W/180Wh)
    Kit555Wh, // (100W/555Wh)
    Kit962Wh, // (100W/962Wh)
    Kit1536Wh, // (1536Wh)
}

export enum CameraPriority {
    Low,
    Medium,
    High,
}
